import {
  ApolloClient,
  ApolloLink,
  DefaultOptions,
  DocumentNode,
  Operation,
  createHttpLink,
  from,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getOperationName } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { GraphQLFormattedError } from "graphql";
import { uniq } from "lodash";

import cache, {
  appVersionVar,
  editorVersionVar,
  errorNotificationVar,
} from "@cache/cache";
import {
  ErrorMatchType,
  defaultNetworkErrorHandler,
  findErrorMatch,
  unexpectedGraphqlErrors,
} from "@components/use-error/use-error";
import { getPusherClient } from "@helpers/pusher";

// Client
const customFetch = (uri: RequestInfo | URL, options: any) => {
  const { operationName } = JSON.parse(options.body);
  return fetch(`${uri}?opname=${operationName}`, options);
};

const customBatchFetch = (uri: RequestInfo | URL, options: any) => {
  const body = JSON.parse(options.body);
  const operationName = uniq(
    body.map(({ operationName }: { operationName: string }) => operationName),
  ).join();
  return fetch(`${uri}?opname=${operationName}`, options);
};

const httpLink = createHttpLink({
  uri: `/graphql`,
  fetch: customFetch,
});

const publicHttpLink = createHttpLink({
  uri: `/public_graphql/`,
  fetch: customFetch,
});

const batchHttpLink = new BatchHttpLink({
  uri: `/graphql`,
  fetch: customBatchFetch,
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = window.TF.csrf;
  const pusher = getPusherClient();
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      [`X-CSRFToken`]: token,
      // https://pusher.com/docs/channels/server_api/excluding-event-recipients/
      // we send this socket id to prevent pusher events to received by user emitting the event.
      [`X-pusher-socket-id`]: pusher?.connection.socket_id,
    },
  };
});

// Convert the value to a number if it is the id field and it is actually a number
const convertIdToInteger = (key: string, value: any) => {
  return key === "id" ? (isNaN(value) ? value : parseInt(value)) : value;
};

const convertIdToIntegerLink = new ApolloLink((operation, forward) => {
  const newOperation = operation;
  return forward(newOperation).map((data) => {
    return JSON.parse(JSON.stringify(data), convertIdToInteger);
  });
});

const appVersionLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    // set app version
    const appVersion = operation
      .getContext()
      .response.headers.get("X-App-Version");
    appVersionVar(appVersion);

    // set editor version
    const newVersion = operation
      .getContext()
      .response.headers.get("X-Editor-Extensions-Version");
    if (newVersion) {
      const currentEditorVersion = editorVersionVar();
      editorVersionVar({
        ...currentEditorVersion,
        oldVersion: currentEditorVersion.oldVersion
          ? currentEditorVersion.oldVersion
          : newVersion,
        newVersion,
      });
    }
    return response;
  });
});

function formattedErrorToError(
  formattedError: GraphQLFormattedError,
  operation: Operation,
): Error {
  const error = new Error(formattedError.message);
  (error as any).name =
    `[GraphQL Error] ${operation.operationName}: ${formattedError.message}`;
  (error as any).path = formattedError.path;
  (error as any).locations = formattedError.locations;
  (error as any).extensions = formattedError.extensions;
  return error;
}

const sentryLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (!Sentry) return forward(operation);

    if (
      graphQLErrors &&
      graphQLErrors.length > 0 &&
      !operation.getContext().skipGraphqlErrorHandling
    ) {
      const errorMatches = (operation.getContext().errorMatches ||
        []) as ErrorMatchType[];

      const firstGraphqlError = graphQLErrors[0];
      const errorMatch = findErrorMatch(
        firstGraphqlError.message,
        errorMatches,
      );

      if (errorMatch) {
        errorNotificationVar(errorMatch);
      } else {
        errorNotificationVar({
          title: "An unexpected error occurred.",
          description: (
            <>
              <div>We have been notified. Please try again.</div>
              {firstGraphqlError?.message && (
                <div className="text-xs mt-4 tracking-tight pl-2 border-l-2 border-gray-100 text-gray-400">
                  {firstGraphqlError.message}
                </div>
              )}
            </>
          ),
        });
      }

      unexpectedGraphqlErrors(graphQLErrors, errorMatches).forEach(
        (graphQLError) => {
          Sentry.captureException(
            formattedErrorToError(graphQLError, operation),
            {
              extra: {
                operationName: operation.operationName,
                variables: operation.variables,
                error: graphQLError,
              },
            },
          );
        },
      );
    }

    if (networkError) {
      defaultNetworkErrorHandler(networkError, operation);
    }

    return forward(operation);
  },
);

const clientDefaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "cache-and-network",
  },
};
const client = new ApolloClient({
  link: from([
    sentryLink,
    authLink,
    convertIdToIntegerLink,
    appVersionLink,
    httpLink,
  ]),
  cache,
  defaultOptions: clientDefaultOptions,
});

export const publicClient = new ApolloClient({
  link: from([
    sentryLink,
    authLink,
    convertIdToIntegerLink,
    appVersionLink,
    publicHttpLink,
  ]),
  cache,
  defaultOptions: clientDefaultOptions,
});

export const batchClient = new ApolloClient({
  link: from([
    sentryLink,
    authLink,
    convertIdToIntegerLink,
    appVersionLink,
    batchHttpLink,
  ]),
  cache,
  defaultOptions: clientDefaultOptions,
});

export const getWatchedQuery = (query: any) => {
  const queries = Array.from(client.getObservableQueries().entries()).map(
    (arr) => arr[1],
  );

  return queries.find(({ queryName }) => queryName === getOperationName(query));
};

export const getWatchedQueries = (query: DocumentNode) => {
  const nonBatchQueries = Array.from(
    client.getObservableQueries().entries(),
  ).map((arr) => arr[1]);
  const batchQueries = Array.from(
    batchClient.getObservableQueries().entries(),
  ).map((arr) => arr[1]);
  const queries = [...nonBatchQueries, ...batchQueries];
  return queries.filter(
    ({ queryName }) => queryName === getOperationName(query),
  );
};

export default client;
